import { OrgField, OrgFieldBlock } from "@ollie-sports/models";

/**
 * Utilities to decide whether a given (field, date, time window) is
 * unavailable due to field-level closures or blocks.
 *
 * These are pure functions — safe to use from server AND client UI.
 */

export function isFieldClosedOnDate(field: OrgField, dateString: string): boolean {
  return !!field.closedDates?.includes(dateString);
}

/** Does a block apply on the given dateString + dayOfWeek? */
export function doesBlockApplyToDate(block: OrgFieldBlock, dateString: string, dayOfWeek: number): boolean {
  if (block.recurringDaysOfWeek?.includes(dayOfWeek)) return true;
  if (block.specificDates?.includes(dateString)) return true;
  if (block.dateRange) {
    const { startDateString, endDateString } = block.dateRange;
    if (dateString >= startDateString && dateString <= endDateString) return true;
  }
  return false;
}

/** "HH:mm" → minutes since midnight */
function hhmmToMins(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Two time windows overlap if their minute ranges intersect. */
export function timesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return hhmmToMins(aStart) < hhmmToMins(bEnd) && hhmmToMins(bStart) < hhmmToMins(aEnd);
}

/**
 * Returns the first field block that overlaps the given slot, or undefined
 * if the field is available during that window.
 */
export function findBlockingField(
  field: OrgField,
  dateString: string,
  dayOfWeek: number,
  slotStartTime: string,
  slotEndTime: string
): OrgFieldBlock | undefined {
  if (!field.blocks) return undefined;
  for (const block of field.blocks) {
    if (!doesBlockApplyToDate(block, dateString, dayOfWeek)) continue;
    if (timesOverlap(slotStartTime, slotEndTime, block.startTime, block.endTime)) return block;
  }
  return undefined;
}

/**
 * Convenience: is the field fully unavailable for a given slot on a given date?
 * Returns the reason string if unavailable, or undefined if available.
 */
export function getFieldUnavailabilityReason(
  field: OrgField,
  dateString: string,
  dayOfWeek: number,
  slotStartTime: string,
  slotEndTime: string
): string | undefined {
  if (isFieldClosedOnDate(field, dateString)) return "Field closed";
  const block = findBlockingField(field, dateString, dayOfWeek, slotStartTime, slotEndTime);
  if (block) return block.reason;
  return undefined;
}

export type FieldClosureForEvent =
  | { type: "closed"; dateString: string }
  | { type: "blocked"; dateString: string; block: OrgFieldBlock };

/**
 * For an event occupying [startDateTime, endDateTime] ("YYYY-MM-DDTHH:mm", in the org's timezone),
 * returns the first closure or block that makes the field unavailable, or undefined if the field is
 * available for the whole event. Multi-day and all-day events are checked day by day, using only the
 * portion of each day the event actually covers. Pure — safe on server and client (used by the
 * event forms to warn admins before they schedule onto a closed field).
 */
export function getFieldClosureForEvent(
  field: OrgField,
  startDateTime: string,
  endDateTime: string,
  isAllDay?: boolean
): FieldClosureForEvent | undefined {
  const [startDate, startTime = "00:00"] = startDateTime.split("T");
  const [endDate, endTime = "23:59"] = endDateTime.split("T");
  if (!startDate || !endDate || endDate < startDate) return undefined;

  // Noon avoids DST edge cases when stepping days.
  const cursor = new Date(`${startDate}T12:00:00`);
  const MAX_DAYS = 31;
  for (let i = 0; i < MAX_DAYS; i++) {
    const dateString = cursor.toISOString().slice(0, 10);
    if (dateString > endDate) break;

    if (isFieldClosedOnDate(field, dateString)) return { type: "closed", dateString };

    const windowStart = !isAllDay && dateString === startDate ? startTime : "00:00";
    const windowEnd = !isAllDay && dateString === endDate ? endTime : "24:00";
    const block = findBlockingField(field, dateString, cursor.getDay(), windowStart, windowEnd);
    if (block) return { type: "blocked", dateString, block };

    cursor.setDate(cursor.getDate() + 1);
  }
  return undefined;
}
